import { API_BASE_URL } from '../config';
import { canonicalCameraAudiences } from './selfHostedCameraApi';

export const MAX_ARCHIVE_PART_BYTES = 64 * 1024 * 1024;
const ID = /^[a-f0-9]{24}$/;
const PART = /^\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}-\d{6}\.mp4$/;
const invalid = () => new Error('Recording response could not be verified. Please retry.');
const validId = value => typeof value === 'string' && ID.test(value);
const date = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{3})?Z$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().replace('.000Z', 'Z') === value.replace('.000Z', 'Z');

export function readOwnerArchiveSession(s, ownerId) {
  if (!s || !validId(s.id) || s.ownerId !== ownerId
    || !['ready', 'live', 'ending', 'finalizing', 'ended', 'failed'].includes(s.state)
    || !date(s.startedAt) || typeof s.audio_enabled !== 'boolean'
    || (s.endedAt != null && !date(s.endedAt)) || (s.observedLiveAt != null && !date(s.observedLiveAt))
    || (s.sourceDojoId != null && !validId(s.sourceDojoId))
    || !s.recording || !['pending', 'complete', 'empty', 'failed', 'partial'].includes(s.recording.state)
    || (s.recording.storage !== undefined && !['uploading', 'saved'].includes(s.recording.storage))
    || (s.recording.storage === 'saved' && s.recording.state !== 'complete')
    || !Number.isSafeInteger(s.recording.parts) || s.recording.parts < 0 || s.recording.parts > 5000
    || (s.recording.state === 'complete' && (s.state !== 'ended' || s.recording.parts === 0))) throw invalid();
  let audiences;
  try { audiences = canonicalCameraAudiences(s.audiences); } catch { throw invalid(); }
  return Object.freeze({ id: s.id, ownerId, state: s.state, startedAt: s.startedAt,
    audiences, audioEnabled: s.audio_enabled, recording: { state: s.recording.state, parts: s.recording.parts, ...(s.recording.storage ? { storage: s.recording.storage } : {}) } });
}

// Uses the real application interceptor. No credentials or public media URLs are
// constructed here. isCurrent also binds same-user login replacements and surfaces.
export function createOwnerCameraArchiveApi({ backendService, identity, ownerId, isCurrent = () => true, timeoutMs = 30000 }) {
  if (!backendService?.client || !identity || !validId(ownerId) || !Number.isFinite(timeoutMs) || timeoutMs < 1 || timeoutMs > 30000) throw invalid();
  const origin = new URL(API_BASE_URL || window.location.origin);
  if (origin.username || origin.password || origin.search || origin.hash || origin.pathname !== '/'
    || !(origin.protocol === 'https:' || (origin.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(origin.hostname)))) throw invalid();
  const assertCurrent = () => {
    if (!isCurrent() || backendService.authenticatedIdentity !== identity || backendService.positionOwner !== ownerId) {
      throw new Error('Account or recording view changed.');
    }
  };
  const checkedId = id => { if (!validId(id)) throw invalid(); return id; };
  async function call(method, path, signal, options = {}) {
    assertCurrent();
    if (signal?.aborted) throw new Error('Recording request cancelled.');
    const abort = new AbortController();
    let rejectCancelled;
    const cancelled = new Promise((_, reject) => { rejectCancelled = reject; });
    const cancel = () => { abort.abort(); rejectCancelled(new Error('Recording request cancelled.')); };
    signal?.addEventListener('abort', cancel, { once: true });
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => { reject(new Error('Recording request timed out. Retry to check the outcome.')); cancel(); }, timeoutMs);
    });
    try {
      const response = await Promise.race([backendService.client.request({ method, url: origin.origin + '/api/camera' + path,
        ...options, signal: abort.signal, timeout: timeoutMs, withCredentials: false,
        katafitEventIdentity: identity, katafitEventOwner: ownerId, katafitEventPosition: null,
        onDownloadProgress: event => { if (options.responseType === 'blob' && event.loaded > MAX_ARCHIVE_PART_BYTES) cancel(); },
      }), deadline, cancelled]);
      assertCurrent();
      if (abort.signal.aborted) throw new Error('Recording request cancelled.');
      return response;
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
  }
  return Object.freeze({ ownerId, assertCurrent,
    async list(cursor = null, signal) {
      if (cursor !== null) checkedId(cursor);
      const response = await call('get', '/sessions', signal, { params: { limit: 20, ...(cursor ? { cursor } : {}) } });
      const body = response.data;
      if (response.status !== 200 || !Array.isArray(body?.sessions) || body.sessions.length > 20
        || (body.nextCursor !== null && !validId(body.nextCursor))) throw invalid();
      const sessions = Array.from(body.sessions, s => readOwnerArchiveSession(s, ownerId));
      if (sessions.some((s, i) => (cursor && s.id >= cursor) || (i > 0 && s.id >= sessions[i - 1].id))
        || (body.nextCursor !== null && (!sessions.length || body.nextCursor !== sessions[sessions.length - 1].id))) throw invalid();
      return { sessions, nextCursor: body.nextCursor };
    },
    async parts(id, signal) {
      const response = await call('get', `/sessions/${checkedId(id)}/recording`, signal);
      const parts = response.data?.parts;
      if (response.status !== 200 || !Array.isArray(parts) || parts.length > 5000) throw invalid();
      const names = new Set();
      return Array.from(parts, p => {
        if (!p || typeof p.name !== 'string' || !PART.test(p.name) || names.has(p.name)
          || !Number.isSafeInteger(p.bytes) || p.bytes <= 0) throw invalid();
        names.add(p.name); return Object.freeze({ name: p.name, bytes: p.bytes });
      });
    },
    async media(id, part, signal) {
      if (!part || !PART.test(part.name) || !Number.isSafeInteger(part.bytes) || part.bytes <= 0 || part.bytes > MAX_ARCHIVE_PART_BYTES) {
        throw new Error('This part exceeds the 64 MiB playback limit for this version.');
      }
      const response = await call('get', `/sessions/${checkedId(id)}/recording/${part.name}`, signal, { responseType: 'blob' });
      const blob = response.data;
      if (response.status !== 200 || !(blob instanceof Blob) || blob.size !== part.bytes || blob.size > MAX_ARCHIVE_PART_BYTES
        || blob.type.split(';')[0] !== 'video/mp4') throw invalid();
      return blob;
    },
    async remove(id, signal) {
      const response = await call('delete', `/sessions/${checkedId(id)}`, signal);
      if (response.data?.deleted !== true || !((response.status === 200 && response.data.cleanup === 'complete')
        || (response.status === 202 && response.data.cleanup === 'pending'))) throw invalid();
      return response.data.cleanup;
    },
  });
}
